<script setup lang="ts">
import type { RouteLocationRaw } from 'vue-router';
import type { Size } from '../types/size';
import type { Target } from '../types/target';
import { computed, resolveComponent } from 'vue';
import Icon from './Icon.vue';

const props = withDefaults(defineProps<{
	label?: string;
	type?: 'button' | 'submit' | 'reset';
	severity?: 'primary' | 'basic';
	size?: Extract<Size, 'sm' | 'md' | 'lg'>;
	isFake?: boolean;
	icon?: string;
	iconPosition?: 'start' | 'end';
	to?: RouteLocationRaw;
	target?: Target;
	external?: boolean;
	loading?: boolean;
	disabled?: boolean;
	testId?: string;
}>(), {
	type: 'button',
	severity: 'primary',
	size: 'md',
	iconPosition: 'end',
});

defineEmits<{
	click: [MouseEvent];
}>();

const NuxtLink = resolveComponent('NuxtLink');

const element = computed(() => {
	if (props.isFake) { return 'span'; }
	if (props.to && !props.disabled) { return NuxtLink; }

	return 'button';
});

const attributes = computed(() => {
	if (props.isFake) { return {}; }

	if (props.to && !props.disabled) {
		return {
			to: props.to,
			target: props.target,
			external: props.external,
			rel: props.target ? 'noopener noreferrer' : undefined,
		};
	}

	return {
		type: props.type,
		disabled: props.disabled || props.loading,
	};
});
</script>

<template>
	<component
		v-bind="attributes"
		class="btn-link"
		:class="[{
			[`btn-link--${props.severity}`]: props.severity,
			[`btn-link--${props.size}`]: props.size,
			'btn-link--loading': props.loading,
			'btn-link--loading-full': props.loading && !props.icon,
			'btn-link--disabled': props.disabled,
		}]"
		:data-testid="props.testId"
		:is="element"
		@click="element === 'button' && $emit('click', $event)"
	>
		<Icon
			:name="props.loading ? 'circle-notch-filled' : props.icon"
			:class="{ 'btn-link__loading': props.loading }"
			v-if="props.icon && props.iconPosition === 'start'"
		/>
		<span class="btn-link__text" v-if="props.label">
			{{ props.label }}
		</span>
		<Icon
			:name="props.loading ? 'circle-notch-filled' : props.icon"
			:class="{ 'btn-link__loading': props.loading }"
			v-if="props.icon && props.iconPosition === 'end'"
		/>
		<Icon
			name="circle-notch-filled"
			class="btn-link__loading"
			v-if="props.loading && !props.icon"
		/>
	</component>
</template>

<style scoped lang="scss">
@layer hm-components
{
	.btn-link
	{
		@include flex(null, center, var(--spacing-2xs), true);
		color:var(--btn-c);
		font-size:var(--btn-fs);
		font-weight:600;

		&:deep(.icon)
		{
			@include size(var(--btn-icon));

			svg path
			{
				vector-effect:non-scaling-stroke;
			}
		}

		&:is(a, button):not(:disabled)
		{
			.btn-link__text
			{
				background:linear-gradient(0deg, var(--btn-c), var(--btn-c)) no-repeat right bottom / 0 1px;
				transition:background-size var(--transition-default);
			}
			@include hover
			{
				.btn-link__text
				{
					background-size:100% 1px;
					background-position-x:left;
				}
			}
			&:active,
			&[data-popper-shown]
			{
				.btn-link__text
				{
					background-size:100% 2px;
				}
			}
		}

		&__text
		{
			@include flex(null, center);
			min-height:var(--btn-icon);
		}
		&__loading
		{
			&:deep(.icon__svg)
			{
				animation:rotate 1s linear infinite;
			}
		}

		// Severity
		&--primary
		{
			--btn-c:var(--btn-empty-c-primary);
		}
		&--basic
		{
			--btn-c:var(--btn-empty-c-dark);
		}

		// Size
		&--sm
		{
			--btn-fs:1.2rem;
			--btn-lh:1.2rem;
			--btn-icon:1.6rem;
		}
		&--md
		{
			--btn-fs:1.4rem;
			--btn-lh:1.6rem;
			--btn-icon:2rem;
		}
		&--lg
		{
			--btn-fs:1.6rem;
			--btn-lh:2rem;
			--btn-icon:2.4rem;
		}

		// Disabled
		&--disabled
		{
			--btn-c:var(--disabled-c);

			cursor:not-allowed;
		}

		// Loading
		&--loading
		{
			cursor:progress;
		}
		&--loading-full
		{
			.btn-link__text
			{
				opacity:0;
			}
			.btn-link__loading
			{
				position:absolute;
				inset:50% auto auto 50%;
				translate:-50% -50%;
				z-index:1;
			}
		}
	}
}
</style>
